/**
 * Shared font registry for the editor's text layers.
 *
 * Single source of truth for the 12 font keys persisted on
 * `layer.fontFamily` (lowercase, STABLE — do not rename). Everything that
 * needs to go from a stored key to a CSS var (client preview), a real font
 * family name (server-side SVG render), or a picker label reads from here —
 * eliminating drift between `EditorPreview.tsx`, `EditorDetailsPanel.tsx`,
 * and the render pipeline.
 *
 * The css var names match `src/styles/globals.css` `--font-*`.
 */

export interface FontEntry {
  /** Stored on layer.fontFamily, lowercase, STABLE. */
  key: string;
  /** Picker display, e.g. "Playfair Display". */
  label: string;
  /** e.g. "var(--font-playfair)" (client preview). */
  cssVar: string;
  /** Real family name for SVG render, e.g. "Playfair Display". */
  family: string;
  /** e.g. "serif" | "sans-serif". */
  fallback: string;
  /**
   * Filename (not a path) of the bundled `.ttf` under `public/editor-fonts/`,
   * used ONLY server-side by the render worker to embed a base64
   * `@font-face` in the exported SVG (see render-worker.ts `buildTextSvg`).
   * Resolve to an absolute path with
   * `path.join(process.cwd(), 'public/editor-fonts', ttfFile)` at read time
   * — never store an absolute path here (breaks across environments).
   * Undefined only if the download failed for this key (none currently).
   */
  ttfFile?: string;
}

// Order matches the picker order from EditorDetailsPanel's previous local
// FONT_OPTIONS array.
export const FONTS: FontEntry[] = [
  {
    key: 'outfit',
    label: 'Outfit',
    cssVar: 'var(--font-outfit)',
    family: 'Outfit',
    fallback: 'sans-serif',
    ttfFile: 'outfit.ttf',
  },
  {
    key: 'inter',
    label: 'Inter',
    cssVar: 'var(--font-inter)',
    family: 'Inter',
    fallback: 'sans-serif',
    ttfFile: 'inter.ttf',
  },
  {
    key: 'montserrat',
    label: 'Montserrat',
    cssVar: 'var(--font-montserrat)',
    family: 'Montserrat',
    fallback: 'sans-serif',
    ttfFile: 'montserrat.ttf',
  },
  {
    key: 'manrope',
    label: 'Manrope',
    cssVar: 'var(--font-manrope)',
    family: 'Manrope',
    fallback: 'sans-serif',
    ttfFile: 'manrope.ttf',
  },
  {
    key: 'roboto',
    label: 'Roboto',
    cssVar: 'var(--font-roboto)',
    family: 'Roboto',
    fallback: 'sans-serif',
    ttfFile: 'roboto.ttf',
  },
  {
    key: 'rubik',
    label: 'Rubik',
    cssVar: 'var(--font-rubik)',
    family: 'Rubik',
    fallback: 'sans-serif',
    ttfFile: 'rubik.ttf',
  },
  {
    key: 'oswald',
    label: 'Oswald',
    cssVar: 'var(--font-oswald)',
    family: 'Oswald',
    fallback: 'sans-serif',
    ttfFile: 'oswald.ttf',
  },
  {
    key: 'noto-sans',
    label: 'Noto Sans',
    cssVar: 'var(--font-noto-sans)',
    family: 'Noto Sans',
    fallback: 'sans-serif',
    ttfFile: 'noto-sans.ttf',
  },
  {
    key: 'space-grotesk',
    label: 'Space Grotesk',
    cssVar: 'var(--font-space-grotesk)',
    family: 'Space Grotesk',
    fallback: 'sans-serif',
    ttfFile: 'space-grotesk.ttf',
  },
  {
    key: 'dm-sans',
    label: 'DM Sans',
    cssVar: 'var(--font-dm-sans)',
    family: 'DM Sans',
    fallback: 'sans-serif',
    ttfFile: 'dm-sans.ttf',
  },
  {
    key: 'playfair',
    label: 'Playfair Display',
    cssVar: 'var(--font-playfair)',
    family: 'Playfair Display',
    fallback: 'serif',
    ttfFile: 'playfair.ttf',
  },
  {
    key: 'bebas',
    label: 'Bebas Neue',
    cssVar: 'var(--font-bebas)',
    family: 'Bebas Neue',
    fallback: 'sans-serif',
    ttfFile: 'bebas.ttf',
  },
];

/** Default entry used when a stored key is unknown/missing. Matches the
 * previous `getLayerFontFamily` default of `var(--font-montserrat)`... but
 * see below: `getLayerFontFamily` keeps its own literal default for byte
 * identical behavior, while `getFontEntry` defaults to `outfit` per the
 * task-1 test contract. */
const DEFAULT_FONT_KEY = 'outfit';

/** key -> cssVar (preview). */
export const FONT_CSS: Record<string, string> = Object.fromEntries(
  FONTS.map((f) => [f.key, f.cssVar]),
);

/** Derived picker options, for existing callers (e.g. EditorDetailsPanel's SelectField). */
export const FONT_OPTIONS: Array<{ value: string; label: string }> = FONTS.map((f) => ({
  value: f.key,
  label: f.label,
}));

/**
 * cssVar + system fallbacks for a stored `layer.fontFamily` key. Moved
 * verbatim from `EditorPreview.tsx` — behavior (including the
 * `var(--font-montserrat)` default for unknown keys) is unchanged.
 */
export function getLayerFontFamily(key: string): string {
  const css = FONT_CSS[key?.toLowerCase?.()] ?? 'var(--font-montserrat)';
  return `${css}, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
}

/** Resolves a stored key to its full FontEntry, defaulting to `outfit` (first entry) when unknown. */
export function getFontEntry(key: string): FontEntry {
  const lower = key?.toLowerCase?.();
  const found = FONTS.find((f) => f.key === lower);
  return found ?? FONTS.find((f) => f.key === DEFAULT_FONT_KEY)!;
}
